/**
 * The pure rules of the onboarding handoff into My Day (B-11; plan §3.2 "Handoff", D17; epic §21, §37, §41.6; cases
 * OB-H-01 to OB-H-05, OB-O-02, OB-G-12). No Angular and no I/O: every answer comes from the arguments (the run takes
 * its arrival signal and scheduler as arguments), so handoff.logic.spec runs under plain Jasmine with a virtual
 * scheduler.
 *
 * The sequence (OB-H-01), from `OnboardingHandoffService.begin` (the Organize screen calls it once the server's
 * Complete answered, marks completion in memory, then navigates to My Day with replaceUrl: D-08's completion contract):
 *  1. `cover`: the overlay (always mounted in app.component, so it spans the route change) shows the dark stage
 *     exactly as Organize left it: the background field gathered (`HANDOFF_FIELD_FROM`, the shell's Organize stage)
 *     and the rail silhouette. It takes the clicks while the screen under it changes.
 *  2. `align`: the field's cards line up into rows (the field on its grid).
 *  3. `rail`: the rail fades.
 *  4. `reveal`: once the cards have aligned AND the router has arrived outside `/app/onboarding` (or after
 *     HANDOFF_ARRIVAL_MAX_WAIT_MS, so the user is never held behind the overlay), the dark stage crossfades away and
 *     the real, light My Day shows with its sidebar (OB-G-12). No hard cut and no Ready page (OB-O-02).
 *  5. `highlight`: the overlay is gone; the tasks onboarding created stay highlighted (`vf-onboarding-new-row`) for
 *     HANDOFF_HIGHLIGHT_MS (OB-H-02), and My Day's first-email coach-mark keeps waiting. FX-7: that time starts once
 *     My Day has drawn a highlighted task's row (OnboardingHandoffService.markHighlightShown; My Day's data can arrive
 *     long after the route change), and never later than HANDOFF_HIGHLIGHT_SHOWN_MAX_WAIT_MS into this phase.
 *  6. `done`: the handoff ends (the highlight goes; the "You're in" hold stays for this sign-in, OB-H-03).
 * Under reduced motion (OB-H-04) every step but the highlight takes 0 ms and the overlay never shows: an instant
 * switch, with nothing flying or fading. The highlight is a still tint, so it stays for the same time.
 *
 * The "You're in / Pin Trovve to Teams" hold (OB-H-03) is keyed to the sign-in, not to plain sessionStorage: the
 * record holds a fingerprint of the sign-in's access token (never the token), so it survives F5 and new tabs of the
 * same sign-in, and it no longer applies once the user signs out (the token is cleared) and signs in again (a new
 * token); the stale record is removed at the next sign-in's first read.
 *
 * Strings and numbers marked † are proposed (the highlight duration is a "few seconds", the item's open risk).
 */
import type { DarkEntryFieldLayout } from '@app/shared/common/dark-entry/dark-entry-field.component';
import { combineLatest, concat, defer, merge, Observable, of, SchedulerLike, timer } from 'rxjs';
import { distinctUntilChanged, map, scan, switchMap, take } from 'rxjs/operators';

// ---------------------------------------------------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------------------------------------------------

/** The overlay shows Organize's stage for this long, so the field's move into rows starts from a painted frame. */
export const HANDOFF_COVER_MS = 60;

/** How long the field's cards take to line up into rows (the field's own move transition is 1.4 s). */
export const HANDOFF_ALIGN_MS = 1400;

/** When the rail starts to fade, counted from the start of the alignment (the cards lead, the rail follows). */
export const HANDOFF_RAIL_DELAY_MS = 700;

/** How long the rail takes to fade. */
export const HANDOFF_RAIL_FADE_MS = 600;

/** How long the dark-to-light crossfade into My Day takes (v9's stage background transition, 1.1 s). */
export const HANDOFF_CROSSFADE_MS = 1100;

/** † How long the created tasks stay highlighted in My Day once it shows ("a few seconds"). */
export const HANDOFF_HIGHLIGHT_MS = 5000;

/**
 * The longest the overlay waits for the router to arrive outside onboarding before it reveals what is there anyway.
 * A failed navigation ends the handoff sooner (the Organize screen's failed exit calls `end`).
 */
export const HANDOFF_ARRIVAL_MAX_WAIT_MS = 10000;

/**
 * † FX-7 (OB-H-02): the longest the highlight waits for My Day to draw a highlighted task's row before its
 * HANDOFF_HIGHLIGHT_MS start anyway (counted from the `highlight` phase), so a My Day that never lists the tasks (or a
 * slow load) never keeps the handoff running for long.
 */
export const HANDOFF_HIGHLIGHT_SHOWN_MAX_WAIT_MS = 30000;

/** The step durations of one handoff. */
export interface HandoffTimeline {
    readonly coverMs: number;
    readonly alignMs: number;
    readonly railDelayMs: number;
    readonly railFadeMs: number;
    readonly crossfadeMs: number;
    readonly highlightMs: number;
    readonly arrivalMaxWaitMs: number;
    /** FX-7: the longest the highlight's time waits for My Day's rows (HANDOFF_HIGHLIGHT_SHOWN_MAX_WAIT_MS). */
    readonly highlightShownMaxWaitMs: number;
}

/** The handoff with motion. */
export const HANDOFF_TIMELINE: HandoffTimeline = Object.freeze({
    coverMs: HANDOFF_COVER_MS,
    alignMs: HANDOFF_ALIGN_MS,
    railDelayMs: HANDOFF_RAIL_DELAY_MS,
    railFadeMs: HANDOFF_RAIL_FADE_MS,
    crossfadeMs: HANDOFF_CROSSFADE_MS,
    highlightMs: HANDOFF_HIGHLIGHT_MS,
    arrivalMaxWaitMs: HANDOFF_ARRIVAL_MAX_WAIT_MS,
    highlightShownMaxWaitMs: HANDOFF_HIGHLIGHT_SHOWN_MAX_WAIT_MS,
});

/**
 * The handoff under reduced motion (OB-H-04): an instant switch; only the still highlight keeps its time, and it waits
 * for My Day's rows like the handoff with motion (FX-7).
 */
export const HANDOFF_TIMELINE_STILL: HandoffTimeline = Object.freeze({
    coverMs: 0,
    alignMs: 0,
    railDelayMs: 0,
    railFadeMs: 0,
    crossfadeMs: 0,
    highlightMs: HANDOFF_HIGHLIGHT_MS,
    arrivalMaxWaitMs: HANDOFF_ARRIVAL_MAX_WAIT_MS,
    highlightShownMaxWaitMs: HANDOFF_HIGHLIGHT_SHOWN_MAX_WAIT_MS,
});

/** The timeline to run: still under reduced motion. */
export function handoffTimeline(reducedMotion: boolean): HandoffTimeline {
    return reducedMotion ? HANDOFF_TIMELINE_STILL : HANDOFF_TIMELINE;
}

// ---------------------------------------------------------------------------------------------------------------------
// Phases
// ---------------------------------------------------------------------------------------------------------------------

/** Where a handoff is (see the file summary). `idle`: no handoff runs. */
export type HandoffPhase = 'idle' | 'cover' | 'align' | 'rail' | 'reveal' | 'highlight' | 'done';

/** The phases in the order a handoff passes them. */
export const HANDOFF_PHASES: readonly HandoffPhase[] = Object.freeze([
    'idle',
    'cover',
    'align',
    'rail',
    'reveal',
    'highlight',
    'done',
] as HandoffPhase[]);

/** The rank of a phase in HANDOFF_PHASES (an unknown value ranks as `idle`). */
export function handoffPhaseRank(phase: HandoffPhase | null | undefined): number {
    const rank: number = HANDOFF_PHASES.indexOf(phase as HandoffPhase);

    return rank < 0 ? 0 : rank;
}

/**
 * The phase after a step arrives: a run only moves forward, so a step that arrives late (an earlier phase, for example
 * when steps of 0 ms land together) changes nothing.
 */
export function advanceHandoffPhase(current: HandoffPhase, next: HandoffPhase): HandoffPhase {
    return handoffPhaseRank(next) > handoffPhaseRank(current) ? next : current;
}

/**
 * One handoff run: the phases from `cover` to `done`, each once and in order (some may be passed over), then complete.
 * FX-7 (OB-H-02): the highlight's HANDOFF_HIGHLIGHT_MS start once My Day has drawn a highlighted task's row (`shown$`),
 * or after `highlightShownMaxWaitMs` from the `highlight` phase, whichever comes first, so the tasks are highlighted
 * for their full time even when My Day's data arrives after the route change.
 *
 * @param timeline the step durations (`handoffTimeline`).
 * @param arrival$ emits when the router has arrived outside onboarding (only the first value counts; it is subscribed
 *        when the run starts, so an arrival during the alignment is not missed).
 * @param shown$ emits when My Day has drawn a highlighted task's row (only the first value counts). It is subscribed at
 *        the `highlight` phase, so it must replay a mark made earlier (OnboardingHandoffService.highlightShown$).
 * @param scheduler the timers' scheduler (asyncScheduler in the app, a virtual one in specs).
 */
export function runHandoff(
    timeline: HandoffTimeline,
    arrival$: Observable<unknown>,
    shown$: Observable<unknown>,
    scheduler: SchedulerLike
): Observable<HandoffPhase> {
    return defer(() => {
        const arrived$: Observable<unknown> = merge(arrival$, timer(timeline.arrivalMaxWaitMs, scheduler)).pipe(
            take(1)
        );
        const aligned$: Observable<number> = timer(timeline.coverMs + timeline.alignMs, scheduler);
        const highlightDone$: Observable<HandoffPhase> = merge(
            shown$,
            timer(timeline.highlightShownMaxWaitMs, scheduler)
        ).pipe(
            take(1),
            switchMap(() => timer(timeline.highlightMs, scheduler)),
            map((): HandoffPhase => 'done')
        );
        const reveal$: Observable<HandoffPhase> = combineLatest([arrived$, aligned$]).pipe(
            take(1),
            switchMap(() =>
                concat(
                    of<HandoffPhase>('reveal'),
                    timer(timeline.crossfadeMs, scheduler).pipe(map((): HandoffPhase => 'highlight')),
                    highlightDone$
                )
            )
        );

        return merge(
            of<HandoffPhase>('cover'),
            timer(timeline.coverMs, scheduler).pipe(map((): HandoffPhase => 'align')),
            timer(timeline.coverMs + timeline.railDelayMs, scheduler).pipe(map((): HandoffPhase => 'rail')),
            reveal$
        ).pipe(scan(advanceHandoffPhase, 'idle' as HandoffPhase), distinctUntilChanged());
    });
}

// ---------------------------------------------------------------------------------------------------------------------
// Arrival
// ---------------------------------------------------------------------------------------------------------------------

/** The onboarding route (D-08's full-screen route; the handoff has arrived once the router is anywhere else). */
export const HANDOFF_ONBOARDING_URL = '/app/onboarding';

/** Whether a router URL is the onboarding route or below it (query and fragment included). */
export function isOnboardingUrl(url: string | null | undefined): boolean {
    if (typeof url !== 'string') {
        return false;
    }
    if (url === HANDOFF_ONBOARDING_URL) {
        return true;
    }
    return ['/', '?', '#', ';'].some((separator: string) => url.indexOf(HANDOFF_ONBOARDING_URL + separator) === 0);
}

/** Whether the router has arrived out of onboarding (a known URL that is not the onboarding route). */
export function hasLeftOnboarding(url: string | null | undefined): boolean {
    return typeof url === 'string' && url.length > 0 && !isOnboardingUrl(url);
}

// ---------------------------------------------------------------------------------------------------------------------
// The overlay view model
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Where the background field starts: the shell's Organize stage (P0-10 `onboardingShellLayout('organize')`: progress
 * 0.8, clustered), so the overlay takes over the dark screen with no visible jump.
 */
export const HANDOFF_FIELD_FROM: { readonly progress: number; readonly layout: DarkEntryFieldLayout } = Object.freeze({
    progress: 0.8,
    layout: 'clustered' as DarkEntryFieldLayout,
});

/** Where the field goes: every card upright on the field's grid, in rows (OB-H-01 "the cards line up into rows"). */
export const HANDOFF_FIELD_TO: { readonly progress: number; readonly layout: DarkEntryFieldLayout } = Object.freeze({
    progress: 1,
    layout: 'scattered' as DarkEntryFieldLayout,
});

/** The rail silhouette's chapter rows (the onboarding rail has exactly five chapters, all done at Organize). */
export const HANDOFF_RAIL_ITEMS: readonly string[] = Object.freeze([
    'chapter-1',
    'chapter-2',
    'chapter-3',
    'chapter-4',
    'chapter-5',
]);

/** The Trovve wordmark of the rail silhouette (the onboarding rail's logo; decorative here). */
export const HANDOFF_RAIL_LOGO_URL = 'https://cdn.trovve.com/trovve/login-left-logo.png';

/**
 * CSS custom properties that carry the step durations to the overlay's stylesheet (bound with `ngStyle`; a type
 * literal, so it fits NgStyle's string-keyed input).
 */
export type HandoffOverlayTiming = {
    readonly '--vf-onboarding-handoff-rail-fade': string;
    readonly '--vf-onboarding-handoff-crossfade': string;
};

/** What the overlay draws (precomputed; the template holds no logic). */
export interface HandoffOverlayVm {
    /** The overlay is on screen (cover, align, rail, reveal; never under reduced motion). */
    readonly isShown: boolean;
    /** The overlay takes the clicks: the screen under it is dark and changing (until the crossfade starts). */
    readonly isBlocking: boolean;
    /** The rail silhouette has faded. */
    readonly isRailGone: boolean;
    /** The dark stage is crossfading into My Day. */
    readonly isRevealing: boolean;
    readonly fieldProgress: number;
    readonly fieldLayout: DarkEntryFieldLayout;
    readonly railItems: readonly string[];
    readonly logoUrl: string;
    readonly timing: HandoffOverlayTiming;
}

const OVERLAY_TIMING: HandoffOverlayTiming = Object.freeze({
    '--vf-onboarding-handoff-rail-fade': HANDOFF_RAIL_FADE_MS + 'ms',
    '--vf-onboarding-handoff-crossfade': HANDOFF_CROSSFADE_MS + 'ms',
});

/** Nothing on screen. */
export const HIDDEN_HANDOFF_OVERLAY: HandoffOverlayVm = Object.freeze({
    isShown: false,
    isBlocking: false,
    isRailGone: false,
    isRevealing: false,
    fieldProgress: HANDOFF_FIELD_FROM.progress,
    fieldLayout: HANDOFF_FIELD_FROM.layout,
    railItems: HANDOFF_RAIL_ITEMS,
    logoUrl: HANDOFF_RAIL_LOGO_URL,
    timing: OVERLAY_TIMING,
});

/** The phases in which the overlay is on screen. */
const SHOWN_PHASES: readonly HandoffPhase[] = Object.freeze(['cover', 'align', 'rail', 'reveal'] as HandoffPhase[]);

/** The overlay of a phase. Reduced motion never shows it (OB-H-04: an instant switch). */
export function buildHandoffOverlayVm(phase: HandoffPhase, reducedMotion: boolean): HandoffOverlayVm {
    const rank: number = handoffPhaseRank(phase);
    const aligned: boolean = rank >= handoffPhaseRank('align');

    if (reducedMotion || SHOWN_PHASES.indexOf(phase) < 0) {
        return HIDDEN_HANDOFF_OVERLAY;
    }
    return Object.freeze({
        isShown: true,
        isBlocking: rank < handoffPhaseRank('reveal'),
        isRailGone: rank >= handoffPhaseRank('rail'),
        isRevealing: phase === 'reveal',
        fieldProgress: aligned ? HANDOFF_FIELD_TO.progress : HANDOFF_FIELD_FROM.progress,
        fieldLayout: aligned ? HANDOFF_FIELD_TO.layout : HANDOFF_FIELD_FROM.layout,
        railItems: HANDOFF_RAIL_ITEMS,
        logoUrl: HANDOFF_RAIL_LOGO_URL,
        timing: OVERLAY_TIMING,
    });
}

// ---------------------------------------------------------------------------------------------------------------------
// The "You're in" hold, keyed to the sign-in (OB-H-03)
// ---------------------------------------------------------------------------------------------------------------------

/** The browser storage key of the hold record (localStorage: every tab of the sign-in shares it). */
export const HANDOFF_HOLD_STORAGE_KEY = 'vf.onboarding.handoff.youreInHold';

/** The record's format version. */
export const HANDOFF_HOLD_VERSION = 1;

/** The stored hold: which sign-in it belongs to. */
export interface HandoffHoldRecord {
    readonly v: number;
    /** `loginSessionKeyOf` the sign-in's access token. */
    readonly session: string;
}

/**
 * The key of a sign-in: a fingerprint of its access token (two FNV-1a 32-bit hashes, base 36), or null without a
 * token. The same token always gives the same key; it is not the token and cannot be turned back into it. A new
 * sign-in (and so an impersonation or a linked-account switch) has a new token, and so a new key.
 */
export function loginSessionKeyOf(accessToken: string | null | undefined): string | null {
    if (typeof accessToken !== 'string' || accessToken.length === 0) {
        return null;
    }
    return fnv1a32(accessToken, 0x811c9dc5).toString(36) + '.' + fnv1a32(accessToken, 0x01000193).toString(36);
}

/** The stored text of a hold for this sign-in. */
export function encodeHandoffHold(sessionKey: string): string {
    const record: HandoffHoldRecord = { v: HANDOFF_HOLD_VERSION, session: sessionKey };

    return JSON.stringify(record);
}

/** The stored hold, or null when the text is missing or is not a hold record of this version. Never throws. */
export function decodeHandoffHold(text: string | null | undefined): HandoffHoldRecord | null {
    let parsed: unknown = null;
    let record: Partial<HandoffHoldRecord>;

    if (typeof text !== 'string' || text.length === 0) {
        return null;
    }
    try {
        parsed = JSON.parse(text);
    } catch {
        return null;
    }
    if (!parsed || typeof parsed !== 'object') {
        return null;
    }
    record = parsed as Partial<HandoffHoldRecord>;
    if (record.v !== HANDOFF_HOLD_VERSION || typeof record.session !== 'string' || record.session.length === 0) {
        return null;
    }
    return Object.freeze({ v: record.v, session: record.session });
}

/** Whether the stored text holds the "You're in" modal for this sign-in (a known key, the same key). */
export function isHandoffHoldFor(text: string | null | undefined, sessionKey: string | null | undefined): boolean {
    const record: HandoffHoldRecord | null = decodeHandoffHold(text);

    return !!record && typeof sessionKey === 'string' && sessionKey.length > 0 && record.session === sessionKey;
}

/**
 * Whether stored text should be removed: there is a record (or unreadable text) and this sign-in is known, but the
 * record is not this sign-in's (it is left from an earlier sign-in: the user signed out since). Without a known
 * sign-in nothing is removed, so a passing read failure never drops a valid hold.
 */
export function isStaleHandoffHold(text: string | null | undefined, sessionKey: string | null | undefined): boolean {
    return (
        typeof text === 'string' &&
        typeof sessionKey === 'string' &&
        sessionKey.length > 0 &&
        !isHandoffHoldFor(text, sessionKey)
    );
}

/** FNV-1a, 32 bit, over UTF-16 code units, from the given offset basis. */
function fnv1a32(text: string, basis: number): number {
    let hash: number = basis >>> 0;

    for (let index = 0; index < text.length; index++) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash >>> 0;
}
